import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import useAdminDashboardBarChart from './AdminDashboardBarChart.hook';
import { chartWrapper, loaderWrapper, tooltipWrapperStyle } from './AdminDashboardBarChart.style';
import { AdminDashboardBarChartDataPropsType } from './AdminDashboardBarChart.types';
import Box from 'yoda-ui/components/Box';
import Loader from 'yoda-ui/components/Loader';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const AdminDashboardBarChart: FC<AdminDashboardBarChartDataPropsType> = ({
  barData,
  loading = false,
  isProductGrouping = false,
}) => {
  const { t } = useTranslation();
  const { chartData, chartNames, colors, resolveProductName, renderLabel, renderCustomY } = useAdminDashboardBarChart(barData);

  return (
    <Box sx={ chartWrapper }>
      {
        loading
          ? <Box sx={ loaderWrapper }><Loader center/></Box>
          : barData.length === 0
            ? <Box sx={ loaderWrapper }>{ t('maya_admin_dashboard_charts_no_data_title') }</Box>
            : <ResponsiveContainer>
              <BarChart
                data={ chartData }
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                {
                  renderCustomY
                    ? <YAxis
                      domain={ [0, 5] }
                      ticks={ [0, 1, 2, 3, 4, 5] }
                    />
                    : <YAxis />
                }
                <Tooltip wrapperStyle={ tooltipWrapperStyle } />
                <Legend />
                {
                  chartNames.map((categoryName, index) => {
                    const label = isProductGrouping
                      ? resolveProductName(categoryName)
                      : categoryName;

                    return (
                      <Bar
                        key={ categoryName }
                        dataKey={ categoryName }
                        stackId="a"
                        fill={ colors[index] }
                        name={ label }
                      >
                        <LabelList
                          dataKey={ categoryName }
                          content={
                            (props) => {
                              const labelData = renderLabel(props);
                              if (!labelData) return null;

                              return (
                                <text
                                  x={ labelData?.x }
                                  y={ labelData?.y }
                                  fill={ YodaColors.black }
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fontSize={ YodaFontSize.xSmall }
                                >
                                  { labelData?.value }
                                </text>
                              );
                            }
                          }
                          position="inside"
                        />
                      </Bar>
                    );
                  })
                }
              </BarChart>
            </ResponsiveContainer>
      }
    </Box>
  );
};

export default AdminDashboardBarChart;
